import React, { useMemo, useState } from 'react';
import {
  Animated,
  Image,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';

import { BRAND } from 'images/RelicVault';
import GuildSidebar from './GuildSidebar';
import VaultHeader from './VaultHeader';
import RaritySection from './RaritySection';
import { CloseIcon, CoinIcon, SearchIcon } from '../shared/RelicVaultIcons';
import { useOneShot } from '../shared/useRelicVaultAnimations';
import { useRelicVaultLayout } from '../shared/useRelicVaultLayout';
import { COLORS, bodyFont, headingFont } from '../shared/theme';
import {
  OWNED_FILTERS,
  buildRaritySections,
  relicsForGuild,
} from 'utils/relicVault/vaultSelectors';
import { IS_WEB, webOnly } from 'utils/relicVault/webStyle';

/**
 * The Relic Vault browser: every guild's full collection, owned vs.
 * undiscovered, searchable and filterable, with a sell-back flow. Shares
 * ownership with the store, so a relic bought there shows as discovered here.
 *
 * The handoff's site nav (Home / Learn / Badges / Guilds), notification bell
 * and "Welcome back" chip are left out - those pages don't exist in the app.
 * The two tabs that do exist, Store and Relic Vault, switch views.
 */
const VaultView = ({ vault, balance, browser, onOpenStore, onClose }) => {
  const { isTablet, isPhone } = useRelicVaultLayout();

  const currentRelics = useMemo(
    () => relicsForGuild(browser.selectedGuildId, vault.ownedRelicIds),
    [browser.selectedGuildId, vault.ownedRelicIds],
  );
  const sections = useMemo(
    () =>
      buildRaritySections(
        currentRelics,
        browser.selectedGuildId,
        browser.searchQuery,
        browser.ownedFilter,
      ),
    [currentRelics, browser.selectedGuildId, browser.searchQuery, browser.ownedFilter],
  );

  const columns = isPhone ? 2 : isTablet ? 3 : 5;
  const gap = isPhone ? 8 : isTablet ? 10 : 13;

  return (
    <View style={styles.root}>
      <TopNav
        balance={balance}
        coinGlow={browser.coinGlow}
        isTablet={isTablet}
        onOpenStore={onOpenStore}
        onClose={onClose}
      />

      <ScrollView
        style={styles.scroll}
        contentContainerStyle={[
          styles.content,
          isTablet && styles.contentTablet,
          isPhone && styles.contentPhone,
        ]}
        keyboardShouldPersistTaps="handled">
        <View style={[styles.layout, isTablet && styles.layoutStacked]}>
          <View style={isTablet ? styles.sidebarStacked : styles.sidebarColumn}>
            <GuildSidebar
              ownedRelicIds={vault.ownedRelicIds}
              ownedCompanionIds={vault.ownedCompanionIds}
              selectedGuildId={browser.selectedGuildId}
              horizontal={isTablet}
              onSelect={browser.selectGuild}
            />
          </View>

          <View style={styles.main}>
            <VaultHeader
              selectedGuildId={browser.selectedGuildId}
              currentRelics={currentRelics}
              isTablet={isTablet}
              isPhone={isPhone}
            />

            <View style={[styles.toolbar, isPhone && styles.toolbarPhone]}>
              <View style={[styles.search, isPhone && styles.searchPhone]}>
                <SearchIcon />
                <TextInput
                  value={browser.searchQuery}
                  onChangeText={browser.setSearchQuery}
                  placeholder="Search relics..."
                  placeholderTextColor={COLORS.dim}
                  autoCorrect={false}
                  style={[styles.searchInput, webOnly({ outlineStyle: 'none' })]}
                />
              </View>
              <View style={[styles.filters, isPhone && styles.filtersPhone]}>
                {OWNED_FILTERS.map(filter => (
                  <FilterPill
                    key={filter.value}
                    label={filter.label}
                    active={browser.ownedFilter === filter.value}
                    onPress={() => browser.setOwnedFilter(filter.value)}
                  />
                ))}
              </View>
            </View>

            {sections.map(section => (
              <RaritySection
                key={section.key}
                section={section}
                selectedRelicId={browser.selectedRelicId}
                columns={columns}
                gap={gap}
                isPhone={isPhone}
                onSelect={browser.selectRelic}
              />
            ))}
          </View>
        </View>
      </ScrollView>
    </View>
  );
};

const TopNav = ({ balance, coinGlow, isTablet, onOpenStore, onClose }) => {
  const glow = useOneShot(coinGlow, 900);
  return (
    <View
      style={[
        styles.topnav,
        isTablet && styles.topnavTablet,
        webOnly({
          backgroundImage: 'linear-gradient(180deg, rgba(22,18,50,0.97), rgba(13,11,32,0.97))',
          backdropFilter: 'blur(14px)',
          WebkitBackdropFilter: 'blur(14px)',
          boxShadow: '0 16px 36px rgba(0,0,0,0.4)',
        }),
      ]}>
      <View style={styles.topnavLeft}>
        <Image
          source={BRAND.badge}
          style={[styles.topnavLogo, webOnly({ filter: 'drop-shadow(0 0 10px rgba(212,232,92,0.4))' })]}
        />
        {!isTablet && <Text style={styles.topnavWord}>QuikLearn</Text>}
        <View style={[styles.navLinks, isTablet && styles.navLinksTablet]}>
          <NavLink label="Store" onPress={onOpenStore} />
          <NavLink label="Relic Vault" active />
        </View>
      </View>

      <View style={styles.topnavRight}>
        <View style={styles.coinPill}>
          {/* coinPillGlow: a gold ring swells and fades after a sale */}
          <Animated.View
            pointerEvents="none"
            style={[
              StyleSheet.absoluteFill,
              styles.coinPillGlow,
              {
                opacity: glow.interpolate({
                  inputRange: [0, 0.35, 1],
                  outputRange: [0, 1, 0],
                }),
              },
              IS_WEB
                ? {
                    boxShadow:
                      '0 0 0 6px rgba(255,216,115,0.35), 0 0 22px 4px rgba(255,216,115,0.55)',
                  }
                : styles.coinPillGlowNative,
            ]}
          />
          <CoinIcon size={18} />
          <Text style={styles.coinBalance}>{balance}</Text>
        </View>
        <TouchableOpacity
          onPress={onClose}
          accessibilityRole="button"
          accessibilityLabel="Close vault"
          style={styles.closeBtn}>
          <CloseIcon size={14} />
        </TouchableOpacity>
      </View>
    </View>
  );
};

const NavLink = ({ label, active, onPress }) => {
  const [hovered, setHovered] = useState(false);
  return (
    <TouchableOpacity
      activeOpacity={0.9}
      disabled={active}
      onPress={onPress}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      accessibilityRole="button"
      accessibilityState={{ selected: !!active }}
      style={[styles.navLink, active && styles.navLinkActive]}>
      <Text
        style={[
          styles.navLinkText,
          hovered && !active && { color: COLORS.lilacLight },
          active && { color: COLORS.text },
        ]}>
        {label}
      </Text>
    </TouchableOpacity>
  );
};

const FilterPill = ({ label, active, onPress }) => {
  const [hovered, setHovered] = useState(false);
  return (
    <TouchableOpacity
      activeOpacity={0.9}
      onPress={onPress}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      accessibilityRole="button"
      accessibilityState={{ selected: active }}
      style={[
        styles.filterPill,
        hovered && !active && styles.filterPillHover,
        active && styles.filterPillActive,
      ]}>
      <Text style={[styles.filterPillText, active && { color: '#FFFFFF' }]}>{label}</Text>
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  root: {
    flex: 1,
  },
  scroll: {
    flex: 1,
  },
  topnav: {
    width: '100%',
    maxWidth: 1392,
    alignSelf: 'center',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 18,
    paddingHorizontal: 24,
    backgroundColor: 'rgba(18,15,42,0.97)',
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(255,255,255,0.09)',
    borderBottomLeftRadius: 16,
    borderBottomRightRadius: 16,
    zIndex: 60,
  },
  topnavTablet: {
    paddingVertical: 14,
    paddingHorizontal: 16,
  },
  topnavLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    flexShrink: 1,
  },
  topnavLogo: {
    width: 36,
    height: 36,
  },
  topnavWord: {
    ...headingFont('800'),
    fontSize: 19,
    color: COLORS.textWarm,
    marginLeft: 10,
  },
  navLinks: {
    flexDirection: 'row',
    alignItems: 'center',
    marginLeft: 32,
  },
  navLinksTablet: {
    marginLeft: 16,
  },
  navLink: {
    paddingBottom: 5,
    borderBottomWidth: 2,
    borderBottomColor: 'transparent',
    marginRight: 26,
  },
  navLinkActive: {
    borderBottomColor: COLORS.accent,
  },
  navLinkText: {
    ...bodyFont('700'),
    fontSize: 14,
    color: COLORS.muted,
  },
  topnavRight: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  coinPill: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(255,255,255,0.04)',
    borderWidth: 1.5,
    borderColor: 'rgba(255,255,255,0.10)',
    borderRadius: 999,
    paddingVertical: 9,
    paddingHorizontal: 16,
  },
  coinPillGlow: {
    borderRadius: 999,
  },
  coinPillGlowNative: {
    borderWidth: 3,
    borderColor: 'rgba(255,216,115,0.6)',
  },
  coinBalance: {
    ...headingFont('700'),
    fontSize: 15,
    color: COLORS.gold,
    marginLeft: 8,
  },
  closeBtn: {
    marginLeft: 12,
    width: 38,
    height: 38,
    borderRadius: 19,
    backgroundColor: 'rgba(255,255,255,0.05)',
    borderWidth: 1.5,
    borderColor: 'rgba(255,255,255,0.10)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  content: {
    width: '100%',
    maxWidth: 1392,
    alignSelf: 'center',
    paddingTop: 26,
    paddingHorizontal: 24,
    paddingBottom: 80,
  },
  contentTablet: {
    paddingTop: 20,
    paddingHorizontal: 16,
    paddingBottom: 64,
  },
  contentPhone: {
    paddingTop: 16,
    paddingHorizontal: 12,
    paddingBottom: 56,
  },
  layout: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  layoutStacked: {
    flexDirection: 'column',
    alignItems: 'stretch',
  },
  sidebarColumn: {
    width: 224,
    marginRight: 20,
  },
  sidebarStacked: {
    marginBottom: 20,
  },
  main: {
    flex: 1,
    minWidth: 0,
  },
  toolbar: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    marginTop: 20,
  },
  toolbarPhone: {
    flexDirection: 'column',
    alignItems: 'stretch',
  },
  search: {
    flexGrow: 1,
    flexShrink: 1,
    minWidth: 220,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(255,255,255,0.04)',
    borderWidth: 1.5,
    borderColor: 'rgba(255,255,255,0.10)',
    borderRadius: 12,
    paddingVertical: 10,
    paddingHorizontal: 14,
    marginRight: 12,
  },
  searchPhone: {
    marginRight: 0,
    minWidth: 0,
  },
  searchInput: {
    ...bodyFont('400'),
    flex: 1,
    fontSize: 13,
    color: COLORS.text,
    marginLeft: 9,
    padding: 0,
  },
  filters: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  filtersPhone: {
    marginTop: 12,
  },
  filterPill: {
    paddingVertical: 8,
    paddingHorizontal: 13,
    borderRadius: 999,
    borderWidth: 1.5,
    borderColor: 'rgba(255,255,255,0.14)',
    marginRight: 7,
  },
  filterPillHover: {
    borderColor: 'rgba(255,255,255,0.3)',
  },
  filterPillActive: {
    backgroundColor: COLORS.accent,
    borderColor: COLORS.accent,
  },
  filterPillText: {
    ...headingFont('700'),
    fontSize: 11,
    color: COLORS.lilacSoft,
  },
});

export default VaultView;
